import React from 'react'

import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import { timeStringToLocal, timeToMs } from '../../utils/dateUtils'
import { hexToRGBA } from '../../utils/graphUtils'
import { Schedule } from '../../types/schedule'

const MINUTE_ACCURACY = 15 // in minutes

// convert time string to index
const timeToIndex = (timestamp: Date | null): number | null => {
  if (timestamp) {
    const date = new Date(timestamp)
    const timeInMs = timeToMs(`${date.getHours()}:${date.getMinutes()}`)
    return Math.floor(timeInMs / 1000 / MINUTE_ACCURACY)
  }
  return null
}

const indexToTime = (index: number | null): string => {
  if (index === null) {
    return ''
  }
  const hour = ('00' + Math.floor((index % ((60 * 24) / MINUTE_ACCURACY)) / (60 / MINUTE_ACCURACY))).slice(-2)
  const minute = ('00' + (index % (60 / MINUTE_ACCURACY)) * MINUTE_ACCURACY).slice(-2)
  return timeStringToLocal(`${hour}:${minute}`)
}

interface TimePickerProps {
  selectedDate: Date | null // ISO-date
  duration: number
  daySchedules: Array<Schedule>
  setSelectedTime: (hour: number, minute: number) => void
}

const TimePicker = (props: TimePickerProps): JSX.Element => {
  const theme = useTheme()
  const selectedTime = timeToIndex(props.selectedDate)

  const calculateUnavailableSlots = (): Array<number> => {
    const unavailableSlots: Array<number> = []
    props.daySchedules.forEach((x) => {
      // convert time + iterations to slots
      const hours = new Date(x.date).getHours()
      const minutes = new Date(x.date).getMinutes()
      const startSlot = hours * (60 / MINUTE_ACCURACY) + Math.floor(minutes / MINUTE_ACCURACY)
      Array.from(Array(x.iterations.length).keys()).forEach((y) => {
        unavailableSlots.push(startSlot + y)
      })
    })
    return unavailableSlots
  }

  const isValidSelection = (selectedIndex: number): boolean => {
    return Array.from(Array(props.duration).keys())
      .map((x) => x + selectedIndex)
      .reduce((acc: boolean, current: number) => acc && calculateUnavailableSlots().indexOf(current) === -1, true)
  }

  return (
    <Stack flex={1}>
      <Stack sx={{ overflowY: 'auto', height: 300, pt: 1, mt: 2 }}>
        <Stack>
          {Array.from(Array((60 * 24) / MINUTE_ACCURACY).keys()).map((_x, index) => {
            const isSelected = selectedTime !== null && index >= selectedTime && index < selectedTime + props.duration
            const isUnavailable = calculateUnavailableSlots().indexOf(index) > -1
            const unavailableBackgroundColor = isUnavailable ? 'text.disabled' : ''
            const selectedBackgroundColor = isSelected ? 'primary.main' : ''
            const conflictBackgroundColor = isSelected && isUnavailable ? 'red' : ''
            const minute = index % (60 / MINUTE_ACCURACY)
            return (
              <Stack key={index} sx={{ height: navigator.maxTouchPoints > 0 ? 35 : 23 }} direction="row">
                <Typography color="text.secondary" sx={{ width: 80, mt: '-11px', textAlign: 'right', pr: 1 }}>
                  {minute === 0 ? indexToTime(index) : ''}
                </Typography>
                <Button
                  variant={selectedBackgroundColor ? 'contained' : 'text'}
                  disabled={!!unavailableBackgroundColor && !conflictBackgroundColor}
                  sx={{
                    flex: 1,
                    borderRadius: 0,
                    backgroundColor:
                      conflictBackgroundColor || unavailableBackgroundColor || selectedBackgroundColor || undefined,
                    borderTop: `1px solid ${
                      minute === 0 ? theme.palette.primary.light : hexToRGBA(theme.palette.primary.light, 0.3)
                    }`,
                  }}
                  aria-label={indexToTime(index)}
                  onClick={() => {
                    if (isValidSelection(index)) {
                      props.setSelectedTime(Math.floor(index / (60 / MINUTE_ACCURACY)), minute * MINUTE_ACCURACY)
                    }
                  }}
                ></Button>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
      {selectedTime !== null && (
        <Typography color="text.secondary" sx={{ textAlign: 'right', pr: 2, pt: 1 }}>{`${indexToTime(
          selectedTime
        )} - ${indexToTime(selectedTime + props.duration)}`}</Typography>
      )}
    </Stack>
  )
}

export default TimePicker
